if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.stayOnPage = this.dataset.stayOnPage === 'true';
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer') && !this.stayOnPage) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        // Sargeantson resilient add-to-cart fix:
        // refresh references after BFCache/theme preview restores and make sure the variant input is active.
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.form = this.querySelector('form');
        if (this.variantIdInput) this.variantIdInput.disabled = false;
        if (this.submitButton.getAttribute('aria-disabled') === 'true' && !this.submitButton.classList.contains('loading')) {
          this.submitButton.removeAttribute('aria-disabled');
        }
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart && !this.stayOnPage) {
              window.location = window.routes.cart_url;
              return;
            }

            if (this.stayOnPage) {
              this.error = false;

              if (this.cart && response.sections) {
                this.cart.getSectionsToRender().forEach((section) => {
                  const sectionElement = section.selector
                    ? document.querySelector(section.selector)
                    : document.getElementById(section.id);

                  const sectionHTML = response.sections[section.id];
                  if (!sectionElement || !sectionHTML) return;

                  const parsed = new DOMParser().parseFromString(sectionHTML, 'text/html');
                  const source = parsed.querySelector(section.selector || '.shopify-section');
                  if (source) sectionElement.innerHTML = source.innerHTML;
                });

                if (this.cart.tagName === 'CART-DRAWER') {
                  this.cart.classList.remove('is-empty');
                  this.cart.querySelector('.drawer__inner')?.classList.remove('is-empty');
                  this.cart.querySelector('cart-drawer-items')?.classList.remove('is-empty');
                  const overlay = this.cart.querySelector('#CartDrawer-Overlay');
                  if (overlay) overlay.addEventListener('click', this.cart.close.bind(this.cart));
                }
              }

              const qtyInput = this.form.querySelector('[name="quantity"]');
              const qtyAdded = Math.max(1, parseInt(qtyInput?.value || '1', 10));
              const originalText = this.submitButtonText.textContent;
              this.submitButtonText.textContent = qtyAdded > 1 ? qtyAdded + ' ADDED ✓' : 'ADDED ✓';
              this.submitButton.classList.add('sargeantson-added');

              window.setTimeout(() => {
                this.submitButtonText.textContent = originalText;
                this.submitButton.classList.remove('sargeantson-added');
              }, 1100);

              if (qtyInput) {
                qtyInput.value = 1;
                qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
              }

              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        const customLabel = this.submitButton.querySelector('[data-sargeantson-button-label]');
        const action = customLabel?.querySelector('.sargeantson-product-button-action');
        const divider = customLabel?.querySelector('.sargeantson-product-button-divider');
        const price = customLabel?.querySelector('.sargeantson-product-button-price');

        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) {
            if (action) {
              action.textContent = text;
              divider?.classList.add('hidden');
              price?.classList.add('hidden');
            } else {
              this.submitButtonText.textContent = text;
            }
          }
        } else {
          this.submitButton.removeAttribute('disabled');
          if (action) {
            action.textContent = window.variantStrings.addToCart;
            divider?.classList.remove('hidden');
            price?.classList.remove('hidden');
          } else {
            this.submitButtonText.textContent = window.variantStrings.addToCart;
          }
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}


/* Sargeantson resilient add-to-cart fix */
(() => {
  const resetProductForms = (root = document) => {
    root.querySelectorAll?.('product-form').forEach((productForm) => {
      const form = productForm.querySelector('form');
      const idInput = form?.querySelector('[name="id"]');
      const button = productForm.querySelector('[type="submit"]');
      const spinner = productForm.querySelector('.loading__spinner');

      if (idInput) idInput.disabled = false;
      if (button) {
        button.removeAttribute('aria-disabled');
        button.classList.remove('loading');
      }
      if (spinner) spinner.classList.add('hidden');
    });
  };

  // Android/Chrome can restore a page from the back-forward cache with a button
  // still marked as loading/disabled. Reset it whenever the page becomes active again.
  window.addEventListener('pageshow', () => resetProductForms(document));
  document.addEventListener('shopify:section:load', (event) => resetProductForms(event.target));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => resetProductForms(document), { once: true });
  } else {
    resetProductForms(document);
  }
})();
